import './App.scss';
import React, { useEffect, useContext, useCallback } from 'react';
import CardetailsForm from './components/CardetailsForm';
import CarconditionForm from './components/CarconditionForm';
import EstimateForm from './components/EstimateForm';
import SellcarForm from './components/SellcarForm';
import useLocalStorage from './hooks/useLocalStorage';
import calculateNewEstimate from './lib/calculateNewEstimate';
import logger from './lib/logger';
import AppSettings from './context/AppSettings';

function App() {

    const initialFormValues = {
        regnr: '',
        mileage: '',
        email: '',
        condition: '',
        phone: '',
        name: '',
        comment: '',
        city: '',
        newsletter: false,
        price_filtered: null,
        price_prediction: null,
        make: '',
        model: '',
        color: '',
        modelYear: '',
        fuelType: '',
        enginePower: '',
        timeFrame: ''

    }

    const initialAppSettings = {
        currentStep: 1,
        validatedSteps: [],
        estimatedCars: [],
        lastUpdate: ''
    }

    const appctx = useContext(AppSettings);

    const ninetyDaysInMilliseconds = 7776000000;
    const [appSettings, setAppSettings] = useLocalStorage('evaluate-car-settings', initialAppSettings, ninetyDaysInMilliseconds)
    const [formValues, setFormValues] = useLocalStorage('evaluate-car', initialFormValues, ninetyDaysInMilliseconds, ()=>{
        setAppSettings(initialAppSettings)
    })


    // Condition changed
    useEffect(() => {
        // Check fields that needs to be set
        const requiredFields = ['condition', 'price_prediction', 'make', 'modelYear', 'mileage'];
        const hasMissingFilds = requiredFields.some(fieldName => formValues[fieldName] === initialFormValues[fieldName]);

        // If all required fields are set, calculate price
        if (!hasMissingFilds) {
            let price = calculateNewEstimate(formValues)
            handleFormUpdates({ price_filtered: price });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formValues.condition, formValues.price_prediction, formValues.manufacturer, formValues.modelYear, formValues.mileage]);


    /**
     * Update current step and goto next step
     * @param integer step 
     * @param object values 
     * @param integer gotoStep 
     */
    function handleSteps(step, values, gotoStep) {
        if (!isStepValidated(step)) {
            const arr = appSettings.validatedSteps;
            arr.push(step);
            setAppSettings(data => ({ ...data, validatedSteps: arr }));
        }
        handleFormUpdates(values);
        setStep(gotoStep);
    }

    /**
     * Set current step
     * @param integer step 
     */
    function setStep(step) {
        setAppSettings(data => ({ ...data, currentStep: step }));
    }

    /**
     * Check if step is validated
     * @param integer step 
     * @returns 
     */
    function isStepValidated(step) {
        const arr = appSettings.validatedSteps;
        if (arr.constructor === Array) {
            if (appSettings.validatedSteps.indexOf(step) >= 0) return true;
        }
        return false;
    }

    /**
     * Check is current step
     * @param integer step 
     * @returns 
     */
    function isCurrentStep(step) {
        if (appSettings.currentStep === step) return true;
        return false;
    }

    /**
     * Get current step status
     * @param integer step 
     * @returns 
     */
    function step(step) {
        if (isCurrentStep(step) && formValues.price_filtered > 0 && step === 3) return 'hasdata';
        if (isCurrentStep(step)) return 'active';
        if (isStepValidated(step)) return 'validated';
        return 'unfilled';
    }

    /**
     * Clear form values
     */
    function clearFormValues() {
        setFormValues(initialFormValues);
        setAppSettings(initialAppSettings);
    }

    /**
     * Handle form updates
     * @param object values
     */
    const handleFormUpdates = useCallback((values) => {
        setFormValues( old => ({ ...old, ...values }));
        logger('updated values', values)
        return true;
    }, [setFormValues]);

    /**
     * Handle cars evaluated, appends regnr to array
     * @param string regnr 
     * @returns boolean
     */
    function handleCarsEvaluated(regnr) {
        let now = Date.now();

        let estCars = appSettings.estimatedCars;
        estCars.push(regnr);
        setAppSettings(data => ({ ...data, estimatedCars: estCars }));
        setAppSettings(data => ({ ...data, lastUpdate: now }));
        return true;
    }

    /**
     * Check number of cars evaluated
     * - If more than 2 cars was evaluated in one day return false
     * @returns boolean
     */
    function checkNoCarsEvaluated() {

        if (appSettings.estimatedCars.length > 2) {
            let timediff = Date.now() - appSettings.lastUpdate;
            let oneDayInMilliseconds = 86400000;

            if (timediff > (oneDayInMilliseconds * 1)) {
                setAppSettings(data => ({ ...data, currentStep: 1, validatedSteps: [] }));
                return true;

            } else {
                return false;
            }
        }

        return true;
    }

    /**
     * Evaluate new car
     * - Clears all forms and resets step to 1
     * @returns void
     */
    function evaluateNewCar() {
        logger('evaluate new car');
        clearFormValues();
        setStep(1);
    };


    if (appSettings.currentStep === 5) {
        setStep(1);
        clearFormValues();
        if (process.env.NODE_ENV === 'development') {
            alert('Redirect to ' + appctx.redirect)
        } else {
            window.location.href = appctx.redirect
        }
    } else {
        return (
            <div className="evaluate-car-container">

                <CardetailsForm
                    setStep={setStep}
                    formValues={formValues}
                    handleFormUpdates={handleFormUpdates}
                    handleSteps={handleSteps}
                    isStepValidated={isStepValidated}
                    view={step(1)}
                />
                <CarconditionForm
                    setStep={setStep}
                    formValues={formValues}
                    handleFormUpdates={handleFormUpdates}
                    handleSteps={handleSteps}
                    isStepValidated={isStepValidated}
                    view={step(2)}

                />
                <EstimateForm
                    setStep={setStep}
                    formValues={formValues}
                    clearFormValues={clearFormValues}
                    handleFormUpdates={handleFormUpdates}
                    handleSteps={handleSteps}
                    isStepValidated={isStepValidated}
                    view={step(3)}
                    handleCarsEvaluated={handleCarsEvaluated}
                    checkNoCarsEvaluated={checkNoCarsEvaluated}
                />
                <SellcarForm
                    setStep={setStep}
                    formValues={formValues}
                    clearFormValues={clearFormValues}
                    handleFormUpdates={handleFormUpdates}
                    handleSteps={handleSteps}
                    isStepValidated={isStepValidated}
                    evaluateNewCar={evaluateNewCar}
                    view={step(4)}
                />
            </div >
        );
    }
}

export default App;
